import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import ReactPlayer from 'react-player';
import { courseService } from '../../api';
import toast from 'react-hot-toast';
import { CheckCircle, FileText, Video, HelpCircle, ChevronLeft, ChevronRight, File } from 'lucide-react';

function LessonPlayer({ lesson, enrollment, onComplete }) {
    const [lessonData, setLessonData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(null);

    useEffect(() => {
        const fetchLessonData = async () => {
            setLoading(true);
            try {
                const [detailsRes, progressRes] = await Promise.all([
                    courseService.getLessonDetail(lesson.id),
                    courseService.getLessonProgress(lesson.id)
                ]);
                setLessonData(detailsRes.data);
                setProgress(progressRes.data);
            } catch (err) {
                console.error('Error fetching lesson data:', err);
                // Fallback to the lesson prop if details fail
                setLessonData(lesson);
            } finally {
                setLoading(false);
            }
        };

        if (lesson?.id) {
            fetchLessonData();
        }
    }, [lesson?.id]);

    const handleToggleCompletion = async () => {
        try {
            const res = await courseService.toggleLessonCompletion(lesson.id);
            setProgress(prev => ({ ...prev, is_completed: res.data.is_completed }));
            if (res.data.is_completed) {
                toast.success('Lesson completed!');
                if (onComplete) onComplete();
            } else {
                toast('Marked as incomplete', { icon: 'ℹ️' });
            }
        } catch (err) {
            toast.error('Failed to update completion status.');
        }
    };

    if (loading) return <div className="p-xl text-center">Loading lesson content...</div>;
    if (!lessonData) return <div className="p-xl text-center">Lesson content not available.</div>;

    const renderContent = () => {
        switch (lessonData.content_type) {
            case 'video':
            case 'youtube_video':
                return (
                    <div className="player-wrapper aspect-video bg-black rounded-2xl overflow-hidden shadow-lg mb-lg">
                        <ReactPlayer
                            url={lessonData.content || lessonData.file_url}
                            controls
                            width="100%"
                            height="100%"
                        />
                    </div>
                );
            case 'markdown':
                return (
                    <div className="markdown-content prose max-w-none bg-theme-card p-lg rounded-2xl shadow-sm border mb-lg">
                        <ReactMarkdown>{lessonData.content_display || lessonData.content}</ReactMarkdown>
                    </div>
                );
            case 'pdf':
                return (
                    <div className="pdf-content bg-theme-card p-lg rounded-2xl shadow-sm border mb-lg flex flex-col items-center gap-md">
                        <File size={64} className="text-primary opacity-20" />
                        <h3 className="font-bold">PDF Document</h3>
                        <p className="text-sm text-muted">This lesson contains a PDF document.</p>
                        <a
                            href={lessonData.file_url || lessonData.content}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary flex items-center gap-xs"
                        >
                            Open PDF in New Tab <ChevronRight size={18} />
                        </a>
                    </div>
                );
            case 'text':
            default:
                return (
                    <div className="text-content bg-theme-card p-lg rounded-2xl shadow-sm border mb-lg whitespace-pre-wrap">
                        {lessonData.content_display || lessonData.content}
                    </div>
                );
        }
    };

    return (
        <div className="lesson-player animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h2 className="text-h4 font-bold text-dark">{lessonData.title}</h2>
                    <div className="flex items-center gap-md text-sm text-muted mt-xs">
                        <span className="flex items-center gap-xs">
                            {lessonData.content_type === 'video' ? <Video size={14} /> : <FileText size={14} />}
                            {lessonData.content_type.charAt(0).toUpperCase() + lessonData.content_type.slice(1)}
                        </span>
                        <span>•</span>
                        <span>{lessonData.duration_minutes} minutes</span>
                    </div>
                </div>

                <button
                    type="button"
                    className={`btn flex items-center gap-xs ${progress?.is_completed ? 'btn-success' : 'btn-tonal'}`}
                    onClick={handleToggleCompletion}
                >
                    {progress?.is_completed ? (
                        <>
                            <CheckCircle size={18} /> Completed
                        </>
                    ) : (
                        <>
                            Mark as Complete
                        </>
                    )}
                </button>
            </div>

            <div className="lesson-body-content">
                {renderContent()}
            </div>

            {lessonData.description && (
                <div className="lesson-description bg-theme-card p-lg rounded-2xl shadow-sm border mt-lg">
                    <h4 className="font-bold mb-xs">Lesson Description</h4>
                    <p className="text-muted text-sm leading-relaxed">{lessonData.description}</p>
                </div>
            )}
        </div>
    );
}

export default LessonPlayer;
